import React from 'react';
import { BookOpen, Rocket, Users, BarChart3, Lightbulb } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';

function MarkdownText({ text }) {
  // Simple markdown: **bold** and \n for line breaks
  const parts = text.split('\n');
  return (
    <div className="space-y-2">
      {parts.map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-2" />;
        // Render bold
        const rendered = line.split(/(\*\*[^*]+\*\*)/).map((seg, j) => {
          if (seg.startsWith('**') && seg.endsWith('**')) {
            return <strong key={j}>{seg.slice(2, -2)}</strong>;
          }
          return <span key={j}>{seg}</span>;
        });
        // Check if it's a list item
        const trimmed = line.trimStart();
        if (trimmed.startsWith('- ') || /^\d+\.\s/.test(trimmed)) {
          return <div key={i} className="pl-4 text-stone-600">{rendered}</div>;
        }
        return <p key={i} className="text-stone-600">{rendered}</p>;
      })}
    </div>
  );
}

function GuideSection({ icon: Icon, title, content, color = 'amber' }) {
  const colors = {
    amber: 'bg-amber-100 text-amber-600',
    purple: 'bg-purple-100 text-purple-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    rose: 'bg-rose-100 text-rose-600',
  };

  return (
    <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
      <h2 className="font-serif text-xl mb-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        {title}
      </h2>
      <MarkdownText text={content} />
    </section>
  );
}

export function UserGuide() {
  const { t } = useTranslation();

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="font-serif text-3xl font-semibold text-stone-900 mb-2">
          {t('guide.title')}
        </h1>
      </div>

      <GuideSection
        icon={BookOpen}
        title={t('guide.whatIsThis')}
        content={t('guide.whatIsThisContent')}
        color="amber"
      />

      <GuideSection
        icon={Rocket}
        title={t('guide.gettingStarted')}
        content={t('guide.gettingStartedContent')}
        color="blue"
      />

      <GuideSection
        icon={Users}
        title={t('guide.panelBuilder')}
        content={t('guide.panelBuilderContent')}
        color="purple"
      />

      <GuideSection
        icon={BarChart3}
        title={t('guide.interpretingResults')}
        content={t('guide.interpretingResultsContent')}
        color="green"
      />

      <GuideSection
        icon={Lightbulb}
        title={t('guide.tips')}
        content={t('guide.tipsContent')}
        color="rose"
      />
    </div>
  );
}
